<script setup>
    import { computed } from 'vue';
    import { useMainStore } from '@/stores/store.js';
    import { PhWarningCircle, PhAsterisk } from '@phosphor-icons/vue';
    import { Validations } from '@/validations.js';
    const store = useMainStore();
</script>
<!--
========================================================================
PROJECT STEP
Collects project name and room name from the user.
This is the second step in the 5-step acoustic analyzer workflow.
========================================================================
-->
<template>
    <div
        id="step-project"
        class="lux-step-root">
        <h2 class="lux-page-title">Project and room</h2>
        <p class="lux-copy-normal">
            Let's start by gathering some information about your project. This will help us label your acoustic report accurately.
        </p>

        <div class="lux-form lux-mt w-full bg-white px-4 py-6 text-black sm:px-12 sm:py-10">
            <!-- <p class="font-semibold uppercase text-black text-lg mb-8">Room Details</p> -->

            <div class="mt-8 grid grid-cols-1 gap-x-6 gap-y-8 first-of-type:mt-0 sm:grid-cols-6">
                <div class="sm:col-span-3 sm:col-start-1">
                    <label
                        for="projectname"
                        class="relative block text-sm font-bold uppercase leading-6 text-black focus:text-accent peer-focus:text-accent">
                        Project name
                        <span class="sr-only">(required)</span>
                        <PhAsterisk
                            weight="bold"
                            class="lux-required-mark"
                            aria-hidden="true" />
                    </label>
                    <div class="mt-2">
                        <input
                            type="text"
                            name="projectname"
                            id="projectname"
                            v-model="store.user.projectname"
                            placeholder="Project name"
                            aria-labelledby="projectname"
                            class="peer block w-full appearance-auto border-0 border-b border-black bg-gray-50 px-1.5 text-gray-900 ring-0 focus:border-accent focus:bg-gray-100 focus:ring-0 sm:text-sm sm:leading-6" />
                    </div>
                    <p
                        class="mt-1 text-sm text-red-600"
                        id="projectname-error"
                        v-if="!store.validateProjectName && store.validation.Project.showErrors">
                        {{ store.validation.Project.errors.projectname }}
                    </p>
                </div>
                <div class="sm:col-span-3">
                    <label
                        for="roomname"
                        class="relative block text-sm font-bold uppercase leading-6 text-black focus:text-accent peer-focus:text-accent">
                        Room name
                        <span class="sr-only">(required)</span>
                        <PhAsterisk
                            weight="bold"
                            class="lux-required-mark"
                            aria-hidden="true" />
                    </label>
                    <div class="mt-2">
                        <input
                            type="text"
                            name="roomname"
                            id="roomname"
                            v-model="store.user.roomname"
                            placeholder="Room name"
                            aria-labelledby="roomname"
                            class="peer block w-full appearance-auto border-0 border-b border-black bg-gray-50 px-1.5 text-gray-900 ring-0 focus:border-accent focus:bg-gray-100 focus:ring-0 sm:text-sm sm:leading-6" />
                    </div>
                    <p
                        class="mt-1 text-sm text-red-600"
                        id="roomname-error"
                        v-if="!store.validateRoomName && store.validation.Project.showErrors">
                        {{ store.validation.Project.errors.roomname }}
                    </p>
                </div>
            </div>
            <div class="mt-10 text-center">
                <button
                    type="button"
                    class="lux-btn lux-btn-small lux-btn-bare font-normal"
                    @click="store.handleClearData(store.user.currentStepName)">
                    Clear Form
                </button>
            </div>
        </div>
        <span class="hidden">
            <input
                type="hidden"
                name="stepProjectValid"
                :value="store.stepProjectIsValid"
                class="hidden" />
        </span>
    </div>
</template>
